import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PeopleSdk } from 'lib-klab-sdks';
import { SessionCtl } from '../../../infra/services/session.ctl.service';
import { EMPLOYEES_PORT } from '../../ports/people/employees.token';
import { EmployeesPort } from '../../ports/people/employees.port';
import { SESSIONS_PORT } from '../../ports/iam/sessions.token';
import { SessionsPort } from '../../ports/iam/sessions.port';

export interface UpdateEmployeeOutcome {
    /** Whether the email of the current logged-in user changed in this update. */
    emailChangedForCurrentUser: boolean;
}

/**
 * Orchestrates the employee update flow:
 *  - Calls people-api PUT /employees/{id}.
 *  - If the updated employee is the current logged-in user and email did NOT change,
 *    refreshes the session so the UI reflects new name/lastname/role/menu without re-login.
 *  - If the email of the current user changed, IAM resets the password and sends new
 *    credentials by email. The use-case logs the user out and signals
 *    emailChangedForCurrentUser=true so the caller can redirect to /login with a toast.
 *  - If the updated employee is NOT the current user, no session changes happen.
 */
@Injectable({ providedIn: 'root' })
export class UpdateEmployeeUseCase {
    constructor(
        @Inject(EMPLOYEES_PORT) private employees: EmployeesPort,
        @Inject(SESSIONS_PORT) private sessions: SessionsPort,
        private sessionCtl: SessionCtl,
    ) { }

    execute(
        employeeId: number,
        payload: PeopleSdk.EmployeeUpdateRequest,
    ): Observable<UpdateEmployeeOutcome> {
        const myEmployeeId = this.sessionCtl.getEmployeeId();
        const isCurrentUser = myEmployeeId !== undefined && employeeId === myEmployeeId;
        const newEmail = (payload as { email?: string })?.email?.trim().toLowerCase();
        const currentEmail = this.sessionCtl.currentUser?.email?.trim().toLowerCase();
        const emailChangedForCurrentUser =
            isCurrentUser && !!newEmail && !!currentEmail && newEmail !== currentEmail;

        return this.employees.updateEmployee(employeeId, payload).pipe(
            switchMap(() => {
                if (!isCurrentUser) {
                    return of({ emailChangedForCurrentUser: false });
                }

                if (emailChangedForCurrentUser) {
                    // IAM ya reseteó la password y mandó nuevas credenciales:
                    // cerramos sesión para forzar re-login con el nuevo email.
                    return this.sessions.logout().pipe(
                        map(() => ({ emailChangedForCurrentUser: true })),
                        catchError(() => of({ emailChangedForCurrentUser: true })),
                    );
                }

                return this.sessions.refreshCurrentSession().pipe(
                    map(() => ({ emailChangedForCurrentUser: false })),
                    catchError(() => of({ emailChangedForCurrentUser: false })),
                );
            }),
        );
    }
}
